import React, { useRef, useEffect, useState, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/bright'
const DEFAULT_CENTER = [-46.6259, -23.55] // Sao Paulo

export default function MapLibreMap({
  center = DEFAULT_CENTER,
  zoom = 14,
  pitch = 0,
  bearing = 0,
  className = '',
  style = {},
  showNavigation = true,
  show3DBuildings = false,
  onMapReady,
  onMapClick,
  children
}) {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [loaded, setLoaded] = useState(false)

  // Initialize map
  useEffect(() => {
    if (map.current) return

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: OPENFREEMAP_STYLE,
      center,
      zoom,
      pitch,
      bearing,
      canvasContextAttributes: { antialias: true }
    })

    if (showNavigation) {
      map.current.addControl(new maplibregl.NavigationControl(), 'top-right')
    }

    map.current.on('load', () => {
      setLoaded(true)

      // Add 3D buildings if requested
      if (show3DBuildings) {
        add3DBuildings(map.current)
      }

      // Notify parent component
      onMapReady?.(map.current)
    })

    if (onMapClick) {
      map.current.on('click', onMapClick)
    }

    return () => {
      map.current?.remove()
      map.current = null
    }
  }, [])

  return (
    <div
      ref={mapContainer}
      className={`h-[400px] w-full rounded border border-gray-300 ${className}`}
      style={style}
    />
  )
}

// Add 3D buildings layer from OpenFreeMap
function add3DBuildings(map) {
  // Find the first symbol layer to insert buildings beneath labels
  const layers = map.getStyle().layers
  let labelLayerId
  for (let i = 0; i < layers.length; i++) {
    if (layers[i].type === 'symbol' && layers[i].layout['text-field']) {
      labelLayerId = layers[i].id
      break
    }
  }

  map.addSource('openfreemap-buildings', {
    url: 'https://tiles.openfreemap.org/planet',
    type: 'vector'
  })

  map.addLayer(
    {
      id: '3d-buildings',
      source: 'openfreemap-buildings',
      'source-layer': 'building',
      type: 'fill-extrusion',
      minzoom: 15,
      filter: ['!=', ['get', 'hide_3d'], true],
      paint: {
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['get', 'render_height'],
          0, 'lightgray',
          200, 'royalblue',
          400, 'lightblue'
        ],
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15, 0,
          16, ['get', 'render_height']
        ],
        'fill-extrusion-base': [
          'case',
          ['>=', ['get', 'zoom'], 16],
          ['get', 'render_min_height'],
          0
        ],
        'fill-extrusion-opacity': 0.8
      }
    },
    labelLayerId
  )
}

// Helper function to add a GeoJSON layer
export function addGeoJsonLayer(map, id, data, options = {}) {
  const {
    type = 'fill',
    color = '#3388ff',
    opacity = 0.5,
    lineWidth = 2,
    lineColor,
    onClick,
    onHover
  } = options

  // Add source
  map.addSource(id, {
    type: 'geojson',
    data
  })

  // Add fill layer
  if (type === 'fill' || type === 'both') {
    map.addLayer({
      id: `${id}-fill`,
      type: 'fill',
      source: id,
      paint: {
        'fill-color': color,
        'fill-opacity': opacity
      }
    })
  }

  // Add line layer
  if (type === 'line' || type === 'both') {
    map.addLayer({
      id: `${id}-line`,
      type: 'line',
      source: id,
      paint: {
        'line-color': lineColor || color,
        'line-width': lineWidth
      }
    })
  }

  // Add click handler
  if (onClick) {
    const layerId = type === 'line' ? `${id}-line` : `${id}-fill`
    map.on('click', layerId, onClick)
    map.on('mouseenter', layerId, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = ''
    })
  }

  // Add hover handler
  if (onHover) {
    const layerId = type === 'line' ? `${id}-line` : `${id}-fill`
    map.on('mouseenter', layerId, onHover)
  }
}

// Helper function to fit map bounds to GeoJSON data
export function fitBoundsToGeoJson(map, geojson, options = {}) {
  const { padding = 50, maxZoom = 18 } = options
  const bounds = new maplibregl.LngLatBounds()

  const addCoordinates = (coords) => {
    if (typeof coords[0] === 'number') {
      bounds.extend(coords)
    } else {
      coords.forEach(addCoordinates)
    }
  }

  if (geojson.type === 'FeatureCollection') {
    geojson.features.forEach(feature => {
      addCoordinates(feature.geometry.coordinates)
    })
  } else if (geojson.type === 'Feature') {
    addCoordinates(geojson.geometry.coordinates)
  } else if (geojson.coordinates) {
    addCoordinates(geojson.coordinates)
  }

  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, { padding, maxZoom })
  }
}

// Helper to create a popup
export function createPopup(map, lngLat, content) {
  return new maplibregl.Popup()
    .setLngLat(lngLat)
    .setHTML(content)
    .addTo(map)
}
